import React from "react";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Paper, { PaperProps } from "@mui/material/Paper";
import { capitalize, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { useReferentialContext } from "../../Context";
import { Maps } from "../../reckon-api";
import { getNeonColor, scrollable } from "../../theme";
import { getMapAsset } from "../../valorant-api/mapAssets";

function mapBannerUrl(map: Maps) {
    if (map.uuid) {
        return `https://media.valorant-api.com/maps/${map.uuid}/listviewicon.png`;
    }
    return `https://res.cloudinary.com/xenesis/image/upload/c_fill,h_48,w_120/valAssets/${map.id}.png`;
}

function MapOptionRow({ map, selected }: { map: Maps; selected?: boolean }) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    return (
        <Stack
            direction="row"
            spacing={1.25}
            sx={{
                alignItems: "center",
                width: "100%",
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: 96,
                    height: 36,
                    flexShrink: 0,
                    overflow: "hidden",
                    borderRadius: "4px",
                    border: selected
                        ? `1px solid ${neonColor}`
                        : "1px solid rgba(255,255,255,0.12)",
                    boxShadow: selected ? `0 0 10px ${neonColor}40` : "none",
                    backgroundColor: "rgba(255,255,255,0.04)",
                }}
            >
                <Box
                    component="img"
                    src={mapBannerUrl(map)}
                    alt={map.id}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: selected
                            ? `linear-gradient(90deg, ${neonColor}33 0%, transparent 70%)`
                            : "linear-gradient(90deg, rgba(0,0,0,0.35) 0%, transparent 60%)",
                        pointerEvents: "none",
                    }}
                />
            </Box>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: selected ? 600 : 500,
                    letterSpacing: "0.4px",
                    color: selected ? neonColor : "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {capitalize(map.id)}
            </Typography>
        </Stack>
    );
}

function NeonAutocompletePaper(props: PaperProps) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    return (
        <Paper
            {...props}
            elevation={0}
            sx={{
                mt: 0.5,
                backgroundColor: "rgba(0, 0, 5, 0.96)",
                backgroundImage: `linear-gradient(145deg, ${neonColor}08 0%, transparent 45%, ${neonColor}05 100%)`,
                border: `1px solid ${neonColor}28`,
                boxShadow: `
                    0px 0px 18px rgba(25, 25, 35, 0.85),
                    0px 0px 28px ${neonColor}18,
                    inset 0px 0px 12px ${neonColor}08
                `,
                backdropFilter: "blur(12px)",
                overflow: "hidden",
                "& .MuiAutocomplete-listbox": {
                    padding: "6px",
                    ...scrollable,
                    maxHeight: 320,
                },
                "& .MuiAutocomplete-option": {
                    borderRadius: "6px",
                    margin: "2px 0",
                    padding: "6px 8px",
                    minHeight: 48,
                    transition: "background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                    '&[aria-selected="true"]': {
                        backgroundColor: `${neonColor}18`,
                    },
                    "&.Mui-focused, &:hover": {
                        backgroundColor: `${neonColor}22`,
                        transform: "translateX(2px)",
                        boxShadow: `inset 3px 0 0 ${neonColor}`,
                    },
                },
                ...props.sx,
            }}
        />
    );
}

export function SingleMapSelect(props: { value: Maps | null; updateValue: (newValue: Maps | null) => void }) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const { mapsReferential } = useReferentialContext();

    if (mapsReferential.length === 0) {
        return (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: 180 }}>
                <CircularProgress size={18} />
                <Typography variant="caption">Loading maps...</Typography>
            </Stack>
        );
    }

    return (
        <Box sx={{ width: 180 }}>
            <Autocomplete
                options={mapsReferential}
                value={props.value}
                onChange={(_event, newValue) => props.updateValue(newValue)}
                getOptionLabel={(option) => capitalize(option.id)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                PaperComponent={NeonAutocompletePaper}
                slotProps={{
                    popper: {
                        sx: {
                            minWidth: 260,
                            "& .MuiAutocomplete-paper": {
                                minWidth: 260,
                            },
                        },
                    },
                }}
                renderOption={(optionProps, option, { selected }) => {
                    const { key, ...rest } = optionProps as typeof optionProps & { key: React.Key };
                    return (
                        <Box component="li" key={key} {...rest}>
                            <MapOptionRow map={option} selected={selected} />
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Map"
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: props.value ? (
                                <>
                                    <Box
                                        component="img"
                                        src={mapBannerUrl(props.value)}
                                        alt={props.value.id}
                                        sx={{
                                            width: 36,
                                            height: 20,
                                            objectFit: "cover",
                                            borderRadius: "3px",
                                            mr: 1,
                                            border: `1px solid ${neonColor}55`,
                                            flexShrink: 0,
                                        }}
                                    />
                                    {params.InputProps.startAdornment}
                                </>
                            ) : (
                                params.InputProps.startAdornment
                            ),
                        }}
                        sx={{
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: neonColor,
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${neonColor}88`,
                            },
                        }}
                    />
                )}
                ListboxProps={{ sx: scrollable }}
            />
        </Box>
    );
}

export function MapBannerSelect(props: {
    value: Maps | null;
    updateValue: (newValue: Maps | null) => void;
    /** When true, an "All" option is shown and the current map can be cleared. */
    allowClear?: boolean;
    height?: number;
    /** Map ids that have data. When set, other maps are greyed out and not selectable. */
    mapsWithGames?: string[];
}) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const { mapsReferential } = useReferentialContext();
    const height = props.height ?? 44;
    const availability = props.mapsWithGames
        ? new Set(props.mapsWithGames.map((id) => id.toLowerCase()))
        : null;

    if (mapsReferential.length === 0) {
        return (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%", height }}>
                <CircularProgress size={18} />
                <Typography variant="caption">Loading maps...</Typography>
            </Stack>
        );
    }

    return (
        <Stack
            direction="row"
            spacing={0.75}
            sx={{
                width: "100%",
                flexShrink: 0,
            }}
        >
            {props.allowClear && (
                <Box
                    onClick={() => props.updateValue(null)}
                    sx={{
                        position: "relative",
                        flex: "1 1 0",
                        minWidth: 0,
                        height,
                        borderRadius: "6px",
                        overflow: "hidden",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255,255,255,0.04)",
                        border: !props.value
                            ? `1px solid ${neonColor}`
                            : "1px solid rgba(255,255,255,0.12)",
                        boxShadow: !props.value ? `0 0 12px ${neonColor}45` : "none",
                        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                            borderColor: `${neonColor}aa`,
                            boxShadow: `0 0 10px ${neonColor}30`,
                        },
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color: !props.value ? neonColor : "rgba(255,255,255,0.75)",
                        }}
                    >
                        All
                    </Typography>
                </Box>
            )}
            {mapsReferential.map((m) => {
                const selected = props.value?.id === m.id;
                const disabled = availability !== null && !availability.has(m.id.toLowerCase());
                const bannerUrl = getMapAsset(m.id, "listViewIcon").replace(
                    "/upload/",
                    "/upload/c_fill,h_72,w_160/"
                );
                return (
                    <Box
                        key={m.id}
                        onClick={() => {
                            if (disabled) return;
                            if (selected && props.allowClear) {
                                props.updateValue(null);
                            } else {
                                props.updateValue(m);
                            }
                        }}
                        sx={{
                            position: "relative",
                            flex: "1 1 0",
                            minWidth: 0,
                            height,
                            borderRadius: "6px",
                            overflow: "hidden",
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled ? 0.35 : 1,
                            border: selected
                                ? `1px solid ${neonColor}`
                                : "1px solid rgba(255,255,255,0.12)",
                            boxShadow: selected && !disabled ? `0 0 12px ${neonColor}45` : "none",
                            transition: "box-shadow 0.2s ease, border-color 0.2s ease, filter 0.2s ease, opacity 0.2s ease",
                            "&:hover": disabled ? undefined : {
                                borderColor: `${neonColor}aa`,
                                boxShadow: `0 0 10px ${neonColor}30`,
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={bannerUrl}
                            alt={m.id}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                                filter: disabled
                                    ? "grayscale(1) brightness(0.45)"
                                    : selected
                                        ? "none"
                                        : "brightness(0.7) saturate(0.9)",
                                transition: "filter 0.2s ease",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background: disabled
                                    ? "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.85) 100%)"
                                    : selected
                                        ? `linear-gradient(180deg, transparent 20%, ${neonColor}55 100%)`
                                        : "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.75) 100%)",
                                pointerEvents: "none",
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 4,
                                px: 0.5,
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                textAlign: "center",
                                color: disabled
                                    ? "rgba(255,255,255,0.45)"
                                    : selected
                                        ? neonColor
                                        : "rgba(255,255,255,0.92)",
                                textShadow: "0 1px 6px rgba(0,0,0,0.9)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {capitalize(m.id)}
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
}
